import { z, ZodError } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum SystemMessageType {
  SYS_PING = 'SYS_PING',
  SYS_PONG = 'SYS_PONG',
  SYS_ERROR = 'SYS_ERROR',
}

// Payload schemas
const systemPingPayloadSchema = z.object({});

const systemPongPayloadSchema = z.object({});

const systemErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string().or(z.instanceof(ZodError)),
  }),
});

// Full message schemas
const systemPingSchema = baseMessageSchema
  .extend({
    type: z.literal(SystemMessageType.SYS_PING),
  })
  .merge(systemPingPayloadSchema);

const systemPongSchema = baseMessageSchema
  .extend({
    type: z.literal(SystemMessageType.SYS_PONG),
  })
  .merge(systemPongPayloadSchema);

const systemErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(SystemMessageType.SYS_ERROR),
  })
  .merge(systemErrorPayloadSchema);

export const systemMessageSchema = z.discriminatedUnion('type', [systemPingSchema, systemPongSchema, systemErrorSchema]);

// Payload types
export type SystemPingPayload = z.infer<typeof systemPingPayloadSchema>;
export type SystemPongPayload = z.infer<typeof systemPongPayloadSchema>;
export type SystemErrorPayload = z.infer<typeof systemErrorPayloadSchema>;

// Full message type
export type SystemMessage = z.infer<typeof systemMessageSchema>;

type PayloadType<T extends SystemMessageType> = T extends SystemMessageType.SYS_PING
  ? SystemPingPayload
  : T extends SystemMessageType.SYS_PONG
    ? SystemPongPayload
    : T extends SystemMessageType.SYS_ERROR
      ? SystemErrorPayload
      : never;

export function generateSystemMessage<T extends SystemMessageType>(type: T, payload: PayloadType<T>): SystemMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case SystemMessageType.SYS_PING:
      return systemPingSchema.parse({
        ...base,
        ...payload,
      });
    case SystemMessageType.SYS_PONG:
      return systemPongSchema.parse({
        ...base,
        ...payload,
      });
    case SystemMessageType.SYS_ERROR:
      return systemErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
